import { apiCustomer } from "@/api"
import { normalizeCouponCode } from "@/lib/pending-coupon"

/** One coupon of the customer's server-side wallet: assigned to them by the store or saved after they typed or received it. */
export type WalletCoupon = { code: string; source: "ASSIGNED" | "SAVED"; expiresAt: string | null }

type Json = Record<string, unknown>

function isObject(value: unknown): value is Json {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/** Reads a wallet body defensively: entries without a code are dropped, codes are normalized and deduplicated. */
export function parseCouponWallet(data: unknown): WalletCoupon[] {
  const list = isObject(data) && Array.isArray(data.coupons) ? data.coupons.filter(isObject) : []
  const seen = new Set<string>()
  return list.flatMap((raw) => {
    const code = normalizeCouponCode(typeof raw.code === "string" ? raw.code : "")
    if (!code || seen.has(code)) return []
    seen.add(code)
    const expiresAt = typeof raw.expiresAt === "string" && raw.expiresAt ? raw.expiresAt : null
    return [{ code, source: raw.source === "ASSIGNED" ? "ASSIGNED" : "SAVED", expiresAt }]
  })
}

/** GET /merchants/:id/coupons/wallet: the signed-in customer's usable coupons at this store; null when it could not be read. */
export async function fetchCouponWallet(merchantId: string): Promise<WalletCoupon[] | null> {
  try {
    const { data } = await apiCustomer.get<unknown>(`/merchants/${merchantId}/coupons/wallet`)
    return parseCouponWallet(data)
  } catch {
    return null
  }
}

/** POST /merchants/:id/coupons/wallet: keeps these codes in the signed-in customer's wallet (unknown or unusable ones are skipped by the server); false when it failed. */
export async function saveWalletCodes(merchantId: string, codes: readonly string[]): Promise<boolean> {
  const wanted = codes.map(normalizeCouponCode).filter((code): code is string => code !== null).slice(0, 20)
  if (wanted.length === 0) return true
  try {
    await apiCustomer.post(`/merchants/${merchantId}/coupons/wallet`, { codes: wanted })
    return true
  } catch {
    return false
  }
}
